import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, withModifiers as _withModifiers, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"
import _imports_0 from '../assets/airobot.png'


const _hoisted_1 = {
  key: 0,
  class: "ai-prompt-wrapper"
}
const _hoisted_2 = ["onKeydown"]
const _hoisted_3 = { class: "chips" }
const _hoisted_4 = ["title", "onClick"]

import axios from "@/axiosInstance";
import { X as XIcon } from "lucide-vue-next";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useMapContext } from '@/composables/useMapContext'; // [NEW] Map Context for AI

/* ---------- Prop: username suggerito da App.vue ---------- */
const FAB_SIZE = 56,
  MARGIN = 12,
  CLICK_TOLERANCE = 5;
const PERSIST_PER_ROUTE = false;

const WHO_AM_I = "/auth/me";

export default /*@__PURE__*/_defineComponent({
  __name: 'AiPrompt',
  props: {
    userName: {}
  },
  setup(__props: any) {

const props = __props;

/* ---------- DRAG state ---------- */
const wrapperRef = ref<HTMLElement | null>(null);
const fabRef = ref<HTMLButtonElement | null>(null);
const promptBoxRef = ref<HTMLElement | null>(null);

const pos = reactive({ x: 0, y: 0 });
const wrapperStyle = computed(() => ({
  left: `${pos.x}px`,
  top: `${pos.y}px`,
}));

const isDragging = ref(false);
let startPointer = { x: 0, y: 0 },
  startPos = { x: 0, y: 0 },
  movedDistance = 0;

const route = useRoute();
const router = useRouter();
const storageKey = computed(
  () => `aiPromptPos:${String(route.name ?? "default")}`
);

// [NEW] Map Context for AI
const { hasContext, getContextForAI, contextSummary } = useMapContext();

function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max);
}
function viewportMaxX() {
  return Math.max(0, window.innerWidth - FAB_SIZE - MARGIN);
}
function viewportMaxY() {
  return Math.max(0, window.innerHeight - FAB_SIZE - MARGIN);
}
function defaultPos() {
  return {
    x: clamp(window.innerWidth - FAB_SIZE - 20, MARGIN, viewportMaxX()),
    y: clamp(window.innerHeight - FAB_SIZE - 20, MARGIN, viewportMaxY()),
  };
}
function loadPos() {
  try {
    const raw = localStorage.getItem(storageKey.value);
    if (raw) {
      const p = JSON.parse(raw);
      pos.x = clamp(+p.x || 0, MARGIN, viewportMaxX());
      pos.y = clamp(+p.y || 0, MARGIN, viewportMaxY());
      return;
    }
  } catch {}
  const d = defaultPos();
  pos.x = d.x;
  pos.y = d.y;
}
function savePos() {
  try {
    localStorage.setItem(
      storageKey.value,
      JSON.stringify({ x: pos.x, y: pos.y })
    );
  } catch {}
}
function resetPos() {
  const d = defaultPos();
  pos.x = d.x;
  pos.y = d.y;
  try {
    localStorage.removeItem(storageKey.value);
  } catch {}
}

/* ---------- Stato chat ---------- */
const show = ref(false);
const isMinimized = ref(localStorage.getItem("ai-chat-minimized") === "true");
const prompt = ref("");
const toast = useToast();

function minimizeChat() {
  isMinimized.value = true;
  show.value = false;
  localStorage.setItem("ai-chat-minimized", "true");
}

function restoreChat() {
  isMinimized.value = false;
  resetPos();
  updateGreetSide();
  localStorage.setItem("ai-chat-minimized", "false");
}

/* Toast helpers */
const ok = (msg: string) =>
  toast.success(msg, { position: "top-right", duration: 2200 });

const successByIntent: Record<string, string> = {
  addNewDevice: "Device creato con successo",
  addNewNode: "Modulo aggiunto",
  createConnection: "Connessione creata",
  moveNode: "Modulo spostato",
  removeNode: "Modulo rimosso",
  other: "Operazione eseguita",
  dashboard: "Ecco la dashboard",
  devices: "Ecco la lista dispositivi",
  data: "Ecco la sezione dati",
  account: "Ecco la pagina account",
};

const samples = ref([
  {
    label: "MB→SAT→LTE",
    text: "Crea un nuovo device con MB → SAT → LTE/5G",
    tip: "Crea catena",
  },
  {
    label: "Aggiungi modulo con coordinate default",
    text: "Sul device (id) aggiungi un modulo (label) con coordinate default",
    tip: "Aggiungi nodo con coordinate default",
  },
  {
    label: "Aggiungi modulo con determinata posizione",
    text: "Sul device (id) aggiungi un modulo (label) alle coordinate (x,y)",
    tip: "Aggiungi nodo in una posizione",
  },
  {
    label: "Collega modulo1→modulo2",
    text: "Nel device (id) collega (modulo1) → (modulo2)",
    tip: "Crea connessione tra moduli",
  },
  {
    label: "Sposta modulo",
    text: "Nel device (id) sposta il modulo (label) a (x, y)",
    tip: "Sposta nodo",
  },
  {
    label: "Rimuovi modulo",
    text: "Nel device (id) rimuovi il modulo (label)",
    tip: "Rimuovi nodo",
  },
  {
    label: "Nuovo device ramificato",
    text: "Crea un nuovo device con MotherBoard collegata a SAT che è collegato a LTE/5G e LORa",
    tip: "Crea device ramificato",
  },
]);

function useSample(text: string, e: MouseEvent) {
  const isShift = e.shiftKey,
    isAlt = e.altKey || e.metaKey;
  prompt.value =
    isAlt && prompt.value.trim() ? `${prompt.value.trim()} ${text}` : text;
  if (isShift) sendPrompt();
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && show.value) {
    show.value = false;
    prompt.value = "";
  }
}
function closeButtonPrompt() {
  minimizeChat();
  prompt.value = "";
}

/* ---------- Greeting esterno ---------- */
const showGreeting = ref(true); // ad ogni accesso
const greetSide = ref<"left" | "right">("left");
function updateGreetSide() {
  const x = Number((pos as any)?.x ?? 0);
  greetSide.value = x < 220 ? "right" : "left";
}

/* === Username da /auth/me (con cache) === */
const nameRef = ref("");

const greetingText = computed(
  () => `Ciao${nameRef.value ? " " + nameRef.value : ""}, come posso aiutarti?`
);

async function resolveDisplayName() {
  // 1) Prop esplicita
  const fromProp = (props.userName || "").trim();
  if (fromProp) {
    nameRef.value = fromProp;
    return;
  }

  // 2) Cache sessione
  const cached = sessionStorage.getItem("auth.me");
  if (cached) {
    try {
      const d = JSON.parse(cached);
      const nm =
        d?.username ||
        d?.name ||
        (d?.email ? String(d.email).split("@")[0] : "");
      if (nm) {
        nameRef.value = nm;
        return;
      }
    } catch {}
  }

  // 3) Chiamata al BE
  try {
    const { data } = await axios.get(WHO_AM_I, { withCredentials: true });
    const nm =
      data?.username ||
      data?.name ||
      (data?.email ? String(data.email).split("@")[0] : "");
    nameRef.value = nm || "";
    // salva una cache minimale
    sessionStorage.setItem(
      "auth.me",
      JSON.stringify({ username: nameRef.value, email: data?.email || null })
    );
  } catch {
    nameRef.value = ""; // fallback: nessun nome
  }
}

/* Chiudi fumetto quando apri la chat */
watch(show, (v) => {
  if (v) showGreeting.value = false;
});
/* Aggiorna lato quando la posizione cambia */
watch(
  () => [pos.x, pos.y],
  () => updateGreetSide()
);
/* Aggiorna nome se cambia la prop */
watch(
  () => props.userName,
  () => resolveDisplayName()
);

/* ---------- Flip sopra/sotto box ---------- */
const dropBelow = ref(false);
watch(show, async (v) => {
  if (!v) return;
  dropBelow.value = false;
  await nextTick();
  const box = promptBoxRef.value;
  if (!box) return;
  const rect = box.getBoundingClientRect();
  if (rect.top < 0 || rect.bottom > window.innerHeight) {
    dropBelow.value = true;
    await nextTick();
  }
});

/* ---------- Route / Resize ---------- */
watch(
  () => route.name,
  () => {
    show.value = false;
    showGreeting.value = true; // ri-mostra ad ogni rientro pagina
    if (PERSIST_PER_ROUTE) loadPos();
    else resetPos();
    updateGreetSide();
    // non richiamiamo /auth/me ad ogni pagina: la cache è sufficiente.
  }
);

function onResize() {
  pos.x = clamp(pos.x, MARGIN, viewportMaxX());
  pos.y = clamp(pos.y, MARGIN, viewportMaxY());
  if (PERSIST_PER_ROUTE) savePos();
  updateGreetSide();
}

/* ---------- Drag handlers ---------- */
function onPointerDown(e: PointerEvent) {
  if (!fabRef.value) return;
  e.preventDefault();
  (fabRef.value as any).setPointerCapture?.(e.pointerId);
  isDragging.value = true;
  startPointer = { x: e.clientX, y: e.clientY };
  startPos = { x: pos.x, y: pos.y };
  movedDistance = 0;
}
function onPointerMove(e: PointerEvent) {
  if (!isDragging.value) return;
  const dx = e.clientX - startPointer.x;
  const dy = e.clientY - startPointer.y;
  movedDistance = Math.max(movedDistance, Math.hypot(dx, dy));
  pos.x = clamp(startPos.x + dx, MARGIN, viewportMaxX());
  pos.y = clamp(startPos.y + dy, MARGIN, viewportMaxY());
}
function onPointerUp(e: PointerEvent) {
  if (!fabRef.value) return;
  (fabRef.value as any).releasePointerCapture?.(e.pointerId);
  const wasDragging = isDragging.value;
  isDragging.value = false;
  if (PERSIST_PER_ROUTE) savePos();
  if (movedDistance <= CLICK_TOLERANCE && wasDragging) {
    show.value = !show.value;
  }
}
function onPointerCancel() {
  isDragging.value = false;
}

/* ---------- Redirect + sendPrompt (immutato) ---------- */
function waitForEditor(timeout = 4000) {
  return new Promise<void>((resolve) => {
    if ((window as any).__editor_ready) return resolve();
    const t = setTimeout(resolve, timeout);
    const onReady = () => {
      clearTimeout(t);
      window.removeEventListener("editor-ready", onReady as EventListener);
      resolve();
    };
    window.addEventListener("editor-ready", onReady as EventListener, {
      once: true,
    });
  });
}
async function handleRedirect(
  redirect?: { name?: string; params?: Record<string, any> } | null
) {
  if (!redirect?.name) return;
  if (redirect.name === "Editor") {
    const deviceId = redirect.params?.deviceId ?? route.params.deviceId;
    await router.push({
      name: "Editor",
      params: deviceId ? { deviceId: String(deviceId) } : {},
      query: { skipDefaults: "1" },
    });
    return;
  }
  if (["Dashboard", "Devices", "Data", "Account"].includes(redirect.name)) {
    await router.push({
      name: redirect.name as any,
      params: redirect.params ?? {},
    });
    return;
  }
  await router.push({
    name: redirect.name as any,
    params: redirect.params ?? {},
  });
}
/* 
	manca logica per eliminare tutte le connessioni 
	manca logica per far eliminare anche tutti i nodi	X
	quando elimino un nodo non elimina automaticamente la connessione che quel nodo ha con un eventuale altro X
	fare in modo che nella chat lei già sappia a quale device ci stiamo riferendo
*/
// async function sendPrompt() {
//   if (!prompt.value.trim()) return;
//   try {
//     console.log("prompt", prompt.value);
//     const { data: ai } = await axios.post("/openai/request-action", {
//       prompt: prompt.value.trim(),
//     });
//     console.log("ai", ai);
//     const ci =
//       ai?.clientInstructions ??
//       (ai?.context
//         ? {
//             context: ai.context ?? null,
//             intent: ai.intent ?? "other",
//             params: ai.params ?? {},
//             missing: Array.isArray(ai?.missing) ? ai.missing : [],
//           }
//         : null);
//     if (ai?.ask || (ci?.missing && ci.missing.length)) {
//       toast.info(ai?.ask || `Mi servono altri dati: ${ci.missing.join(", ")}`, {
//         position: "top-right",
//         duration: 2500,
//       });
//       return;
//     }
//     if (ai?.redirect) await handleRedirect(ai.redirect);
//     if (ci) {
//       if (ci.context === "Editor") {
//         if (!ci.params?.deviceId && route.params.deviceId)
//           ci.params = { ...ci.params, deviceId: String(route.params.deviceId) };
//         if (ci.intent === "addNewDevice") {
//           const spec = ci.params?.graph;
//           const name =
//             ci.params?.name ?? `AI Device ${new Date().toLocaleString()}`;
//           if (!spec?.nodes?.length) {
//             toast.error("Nessun grafo da creare: mancano i nodi.", {
//               position: "top-right",
//             });
//             return;
//           }
//           const { data: device } = await axios.post("/devices/new-device");
//           const deviceId = device.id;
//           await router.push({
//             name: "Editor",
//             params: { deviceId: String(deviceId) },
//             query: { skipDefaults: "1" },
//           });
//           await waitForEditor();
//           await nextTick();
//           window.dispatchEvent(
//             new CustomEvent("ai-editor-refresh", {
//               detail: { deviceId, graphSpec: spec, name },
//             })
//           );

//           ok(successByIntent.addNewDevice);
//           show.value = false;
//           prompt.value = "";
//           return;
//         }

//         const deviceId = ci.params?.deviceId;
//         if (!deviceId) {
//           toast.error("Serve un deviceId (es. 'sul device 27 …').", {
//             position: "top-right",
//           });
//           return;
//         }
//         await router.push({
//           name: "Editor",
//           params: { deviceId: String(deviceId) },
//           query: { skipDefaults: "1" },
//         });
//         await waitForEditor();
//         await nextTick();
//         window.dispatchEvent(
//           new CustomEvent("ai-editor-command", { detail: ci })
//         );

//         const intentKey = String(ci.intent || "other"); // es. "addNewNode", "createConnection"…
//         ok(successByIntent[intentKey] ?? successByIntent.other);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//       if (ci.context === "Dashboard") {
//         await router.push({ name: "Dashboard" });

//         ok(successByIntent.dashboard);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//       if (ci.context === "Devices") {
//         await router.push({ name: "Devices" });

//         ok(successByIntent.devices);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//       if (ci.context === "Data") {
//         const deviceId = ci.params?.deviceId;
//         await router.push(
//           deviceId
//             ? { name: "Data", params: { deviceId: String(deviceId) } }
//             : { name: "Data" }
//         );

//         ok(successByIntent.data);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//       if (ci.context === "Account") {
//         await router.push({ name: "Account" });

//         ok(successByIntent.account);
//         show.value = false;
//         prompt.value = "";
//         return;
//       }
//     }
//     window.dispatchEvent(new CustomEvent("ai-response", { detail: ai }));
//   } catch (e) {
//     console.error("Errore nella richiesta AI:", e);
//     toast.error("Errore richiesta AI", { position: "top-right" });
//   }
// }

// ——— helpers locali ———
function toOps(ai: any) {
  if (Array.isArray(ai?.operations) && ai.operations.length)
    return ai.operations;
  if (ai?.clientInstructions) return [ai.clientInstructions];
  if (ai?.context) {
    return [
      {
        context: ai.context ?? null,
        intent: ai.intent ?? "other",
        params: ai.params ?? {},
        missing: Array.isArray(ai?.missing) ? ai.missing : [],
      },
    ];
  }
  return [];
}

function ensureDeviceIdOnOps(ops: any[], fallbackId?: string | number) {
  const devId = fallbackId != null ? String(fallbackId) : undefined;
  return ops.map((op) => {
    if (op?.context === "Editor") {
      const p = op.params ?? {};
      if (!p.deviceId && devId) op.params = { ...p, deviceId: devId };
    }
    return op;
  });
}

// ——— DROP-IN sendPrompt ———
async function sendPrompt() {
  if (!prompt.value.trim()) return;
  const currentEditorDeviceId =
    route.name === "Editor" && route.params.deviceId
      ? String(route.params.deviceId)
      : undefined;

  try {
    // [NEW] Build prompt with map context if available
    const mapCtx = hasContext.value ? getContextForAI() : '';
    const fullPrompt = mapCtx 
      ? `${mapCtx}\n\nDOMANDA UTENTE: ${prompt.value.trim()}`
      : prompt.value.trim();
    
    const { data: ai } = await axios.post("/openai/request-action", {
      prompt: fullPrompt,
      context: { currentEditorDeviceId, hasMapContext: hasContext.value },
    });
    console.log("prompt", prompt);
    console.log("risposta ai", ai);
    // 1) chiede slot mancanti / domande follow-up
    const ci =
      ai?.clientInstructions ??
      (ai?.context
        ? {
            context: ai.context ?? null,
            intent: ai.intent ?? "other",
            params: ai.params ?? {},
            missing: Array.isArray(ai?.missing) ? ai.missing : [],
          }
        : null);

    if (ai?.ask || (ci?.missing && ci.missing.length)) {
      toast.info(ai?.ask || `Mi servono altri dati: ${ci.missing.join(", ")}`, {
        position: "top-right",
        duration: 2500,
      });
      return;
    }

    // 2) redirect (se presente)
    if (ai?.redirect) await handleRedirect(ai.redirect);

    // 3) normalizza in lista di operazioni
    let ops = toOps(ai);
    // fallback: se non arriva nulla ma avevamo la forma "ci", trattala come singola op
    if (!ops.length && ci) ops = [ci];

    // 3.a aggiungi deviceId mancante dalle route params
    ops = ensureDeviceIdOnOps(ops, route.params.deviceId as string | undefined);

    // 4) Se tra le ops c’è un addNewDevice con graph, gestiscilo qui
    //    (crea il device, carica editor, applica graph via evento "ai-editor-refresh")
    for (const op of ops) {
      if (op?.context === "Editor" && op?.intent === "addNewDevice") {
        const spec = op.params?.graph;
        const name =
          op.params?.name ?? `AI Device ${new Date().toLocaleString()}`;

        if (!spec?.nodes?.length) {
          toast.error("Nessun grafo da creare: mancano i nodi.", {
            position: "top-right",
          });
          return;
        }

        const { data: device } = await axios.post("/devices/new-device");
        const deviceId = device.id;
        await router.push({
          name: "Editor",
          params: { deviceId: String(deviceId) },
          query: { skipDefaults: "1" },
        });
        await waitForEditor();
        await nextTick();
        window.dispatchEvent(
          new CustomEvent("ai-editor-refresh", {
            detail: { deviceId, graphSpec: spec, name },
          })
        );

        ok("Device creato con successo");
        show.value = false;
        prompt.value = "";
        return; // le altre ops (se esistono) non si applicano allo stesso addNewDevice
      }
    }

    // 5) Per tutte le altre operazioni Editor (addNewNode / removeNode / createConnection / moveNode):
    //    invia un unico payload con operations al listener dell’Editor (batch)
    const editorOps = ops.filter((op: any) => op?.context === "Editor");
    const nonEditorOps = ops.filter(
      (op: any) => op?.context && op.context !== "Editor"
    );

    // esegui eventuali redirect “non Editor” (Dashboard/Data/Devices/Account)
    for (const op of nonEditorOps) {
      const name = op.context;
      if (name === "Dashboard") {
        await router.push({ name: "Dashboard" });
        ok("Ecco la dashboard");
      } else if (name === "Devices") {
        await router.push({ name: "Devices" });
        ok("Ecco la lista dispositivi");
      } else if (name === "Data") {
        const deviceId = op.params?.deviceId;
        await router.push(
          deviceId
            ? { name: "Data", params: { deviceId: String(deviceId) } }
            : { name: "Data" }
        );
        ok("Ecco la sezione dati");
      } else if (name === "Account") {
        await router.push({ name: "Account" });
        ok("Ecco la pagina account");
      }
    }

    if (editorOps.length) {
      // prendi un deviceId “di riferimento” dalla prima op Editor
      const firstDevId = editorOps.find((o: any) => o?.params?.deviceId)?.params
        ?.deviceId;
      if (!firstDevId) {
        toast.error("Serve un deviceId (es. 'sul device 27 …').", {
          position: "top-right",
        });
        return;
      }
      await router.push({
        name: "Editor",
        params: { deviceId: String(firstDevId) },
        query: { skipDefaults: "1" },
      });
      await waitForEditor();
      await nextTick();

      // invia TUTTE le operazioni in un colpo solo (batch)
      window.dispatchEvent(
        new CustomEvent("ai-editor-command", {
          detail: { operations: editorOps },
        })
      );

      // feedback ottimistico
      const intents = new Set<string>(
        editorOps.map((o: any) => String(o.intent || "other"))
      );
      // scegli il messaggio migliore se c’è una sola intenzione, altrimenti “Operazioni eseguite”
      if (intents.size === 1) {
        const k = intents.values().next().value as string;
        ok(successByIntent[k] ?? successByIntent.other);
      } else {
        ok("Operazioni eseguite");
      }
    }

    // 6) chiudi UI
    show.value = false;
    prompt.value = "";
    // facoltativo: emit debug completo
    window.dispatchEvent(new CustomEvent("ai-response", { detail: ai }));
  } catch (e) {
    console.error("Errore nella richiesta AI:", e);
    toast.error("Errore richiesta AI", { position: "top-right" });
  }
}

/* ---------- lifecycle ---------- */
onMounted(() => {
  if (PERSIST_PER_ROUTE) loadPos();
  else resetPos();
  updateGreetSide();
  showGreeting.value = true; // ad ogni accesso
  resolveDisplayName(); // ⇐ prende il nome da /auth/me (con cache)

  window.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onResize);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", onResize);
});

return (_ctx: any,_cache: any) => {
  return (!isMinimized.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", {
          ref_key: "wrapperRef",
          ref: wrapperRef,
          class: _normalizeClass(["ai-floating-chat", { open: show.value }]),
          style: _normalizeStyle(wrapperStyle.value)
        }, [
          _createVNode(_Transition, { name: "ai-greet" }, {
            default: _withCtx(() => [
              (showGreeting.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(['greeting-bubble', greetSide.value]),
                    role: "status",
                    "aria-live": "polite"
                  }, [
                    _createElementVNode("p", null, _toDisplayString(greetingText.value), 1)
                  ], 2))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createElementVNode("div", {
            class: _normalizeClass(["fab-container", { dragging: isDragging.value }])
          }, [
            _createElementVNode("button", {
              ref_key: "fabRef",
              ref: fabRef,
              class: "ai-toggle-btn",
              onPointerdown: onPointerDown,
              onPointermove: onPointerMove,
              onPointerup: onPointerUp,
              onPointercancel: onPointerCancel,
              "aria-label": "Apri assistente AI",
              title: "Trascina per spostare • Clic per aprire"
            }, [...(_cache[3] || (_cache[3] = [
              _createElementVNode("img", {
                src: _imports_0,
                alt: "Ai Icon"
              }, null, -1)
            ]))], 544),
            (!show.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "fab-close-btn",
                  onClick: _withModifiers(minimizeChat, ["stop"]),
                  title: "Minimizza assistente"
                }, [
                  _createVNode(_unref(XIcon))
                ]))
              : _createCommentVNode("", true)
          ], 2),
          (show.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                ref_key: "promptBoxRef",
                ref: promptBoxRef,
                class: _normalizeClass(["ai-prompt-box", dropBelow.value ? 'below' : 'above'])
              }, [
                _createElementVNode("button", {
                  class: "ai-close-btn",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (closeButtonPrompt())),
                  "aria-label": "Chiudi",
                  title: "Chiudi"
                }, [
                  _createVNode(_unref(XIcon))
                ]),
                _withDirectives(_createElementVNode("textarea", {
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((prompt).value = $event)),
                  placeholder: "Scrivi il tuo comando…",
                  onKeydown: [
                    _withKeys(_withModifiers(sendPrompt, ["exact","prevent"]), ["enter"]),
                    _withKeys(_withModifiers(sendPrompt, ["meta","prevent"]), ["enter"]),
                    _withKeys(_withModifiers(sendPrompt, ["ctrl","prevent"]), ["enter"]),
                    _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (show.value = false), ["prevent","stop"]), ["esc"]))
                  ]
                }, null, 40, _hoisted_2), [
                  [_vModelText, prompt.value]
                ]),
                _createElementVNode("div", _hoisted_3, [
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "chips-label" }, "Esempi rapidi", -1)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(samples.value, (s, i) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: i,
                      class: "chip",
                      title: s.tip,
                      onClick: ($event: any) => (useSample(s.text, $event))
                    }, _toDisplayString(s.label), 9, _hoisted_4))
                  }), 128))
                ]),
                _createElementVNode("button", {
                  class: "send-btn",
                  onClick: sendPrompt
                }, "Invia")
              ], 2))
            : _createCommentVNode("", true)
        ], 6)
      ]))
    : (_openBlock(), _createElementBlock("button", {
        key: 1,
        class: "ai-chat-tab",
        onClick: restoreChat,
        title: "Apri assistente AI"
      }, [...(_cache[5] || (_cache[5] = [
        _createElementVNode("div", { class: "tab-icon" }, [
          _createElementVNode("img", {
            src: _imports_0,
            alt: "Ai Icon"
          })
        ], -1),
        _createElementVNode("span", null, "Assistente AI", -1)
      ]))]))
}
}

})